import React, { useEffect } from 'react';
import { Artwork } from '../../types';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

interface LightboxProps {
  artwork: Artwork | null;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ artwork, onClose, onNext, onPrev }) => {
  const { t } = useTranslation();
  const [touchStart, setTouchStart] = React.useState(0);
  const [touchEnd, setTouchEnd] = React.useState(0);

  // Prevent scrolling when lightbox is open
  useEffect(() => {
    if (artwork) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [artwork]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!artwork) return;
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [artwork, onClose, onNext, onPrev]);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;
    
    if (isLeftSwipe && onNext) {
      onNext();
    } else if (isRightSwipe && onPrev) {
      onPrev();
    }
    
    setTouchStart(0);
    setTouchEnd(0);
  };

  if (!artwork) return null;

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-xl"
        onClick={onClose}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {/* Close Button */}
        <button
          className="absolute top-4 right-4 p-2 text-white/70 hover:text-white bg-black/50 rounded-full hover:bg-black/80 transition-all z-10"
          onClick={onClose}
        >
          <X className="w-6 h-6" />
        </button>

        {/* Navigation Buttons */}
        {onPrev && (
          <button
            className="absolute left-4 p-3 text-white/70 hover:text-white bg-black/50 rounded-full hover:bg-black/80 transition-all z-10"
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}
        
        {onNext && (
          <button
            className="absolute right-4 p-3 text-white/70 hover:text-white bg-black/50 rounded-full hover:bg-black/80 transition-all z-10"
            onClick={(e) => { e.stopPropagation(); onNext(); }}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Image Container */}
        <div 
          className="relative max-w-[90vw] max-h-[90vh] flex flex-col items-center"
          onClick={(e) => e.stopPropagation()} // Prevent click from closing
        >
          <motion.img
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            src={artwork.imageUrl}
            alt={t(`artworks.${artwork.id}.title`)}
            className="max-w-full max-h-[80vh] object-contain rounded-sm shadow-2xl"
          />
          
          {/* Info bar */}
          <motion.div 
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mt-4 text-center max-w-xl"
          >
            <h2 className="text-xl font-bold text-white mb-1">
              {t(`artworks.${artwork.id}.title`)}
              <span className="ml-3 text-sm font-normal text-white/50">{artwork.date}</span>
            </h2>
            <p className="text-sm text-slate-300 mb-2">{t(`artworks.${artwork.id}.description`)}</p>
            <div className="flex gap-2 justify-center">
              {artwork.tags.map(tag => (
                <span key={tag} className="text-xs uppercase font-bold text-brand-amber">
                  #{t(`tags.${tag}`, { defaultValue: tag })}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default Lightbox;
